/**
 * HighlightText Component
 * Highlights search terms within text
 */

'use client';

interface HighlightTextProps {
  text: string;
  searchTerm: string;
  className?: string;
}

export function HighlightText({ text, searchTerm, className = '' }: HighlightTextProps) {
  if (!searchTerm || !text) {
    return <span className={className}>{text}</span>;
  }

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) {
    return <span className={className}>{text}</span>;
  }

  // Split text while preserving case
  const parts: Array<{ text: string; isHighlight: boolean }> = [];
  let currentIndex = 0;
  const lowerText = text.toLowerCase();

  while (currentIndex < text.length) {
    const matchIndex = lowerText.indexOf(normalizedSearchTerm, currentIndex);

    if (matchIndex === -1) {
      // No more matches, add remaining text
      parts.push({
        text: text.slice(currentIndex),
        isHighlight: false,
      });
      break;
    }

    // Add text before match
    if (matchIndex > currentIndex) {
      parts.push({
        text: text.slice(currentIndex, matchIndex),
        isHighlight: false,
      });
    }

    // Add matched text
    parts.push({
      text: text.slice(matchIndex, matchIndex + normalizedSearchTerm.length),
      isHighlight: true,
    });

    currentIndex = matchIndex + normalizedSearchTerm.length;
  }

  return (
    <span className={className}>
      {parts.map((part, index) =>
        part.isHighlight ? (
          <mark
            key={index}
            className="bg-yellow-200 text-foreground font-medium rounded px-0.5"
          >
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        )
      )}
    </span>
  );
}

